import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import type {
    DataProtectionMeasure,
    DataSubject,
    DisclosureEvent,
    Document,
    ErasurePreview,
    ErasureRequest,
    PersonalData,
    RetentionRule,
    User,
} from '../api/schema'

/** GDPR hooks (spec 2026-10-03-02). Every route needs `data_protection.read`. */

export type RetentionState = 'running' | 'due' | 'held' | 'undefined'
export type RetentionRuleInput = Omit<RetentionRule, 'uid' | 'origin' | 'created_at' | 'updated_at' | 'catalogue'>

function fail(error: unknown): never {
    const message = (error as { message?: string } | undefined)?.message
    throw new Error(message || 'Request failed')
}

export function useMeasures(org: string, enabled = true) {
    return useQuery({
        queryKey: ['dp-measures', org],
        enabled,
        queryFn: async (): Promise<DataProtectionMeasure[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/data-protection-measures', {
                params: { path: { orgCode: org }, query: { active: true } },
            })
            if (error) fail(error)
            return data ?? []
        },
    })
}

export function useRetentionRules(org: string, enabled = true) {
    return useQuery({
        queryKey: ['dp-retention-rules', org],
        enabled,
        queryFn: async (): Promise<RetentionRule[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/retention-rules', {
                params: { path: { orgCode: org } },
            })
            if (error) fail(error)
            return data ?? []
        },
    })
}

export function useSaveRetentionRule(org: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async ({ uid, body }: { uid?: string; body: RetentionRuleInput }) => {
            const res = uid
                ? await apiClient.PUT('/api/v1/organizations/{orgCode}/retention-rules/{ruleUid}', {
                      params: { path: { orgCode: org, ruleUid: uid } },
                      body,
                  })
                : await apiClient.POST('/api/v1/organizations/{orgCode}/retention-rules', {
                      params: { path: { orgCode: org } },
                      body,
                  })
            if (res.error) fail(res.error)
            return res.data as RetentionRule
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['dp-retention-rules', org] })
            void qc.invalidateQueries({ queryKey: ['dp-retention'] })
        },
    })
}

/** Documents in one retention state (`documents.read`). */
export function useDocumentsByRetention(org: string, state: RetentionState, enabled = true) {
    return useQuery({
        queryKey: ['dp-retention', 'documents', org, state],
        enabled,
        queryFn: async () => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/documents', {
                params: { path: { orgCode: org }, query: { retention_state: state, limit: 50 } as never },
            })
            if (error) fail(error)
            const page = data as unknown as { data: Document[]; total: number }
            return { items: page?.data ?? [], total: page?.total ?? 0 }
        },
    })
}

/** Contacts in one retention state. */
export function useContactsByRetention(org: string, state: RetentionState, enabled = true) {
    return useQuery({
        queryKey: ['dp-retention', 'contacts', org, state],
        enabled,
        queryFn: async () => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/users', {
                params: { path: { orgCode: org }, query: { type: 'contact', retention_state: state, limit: 50 } as never },
            })
            if (error) fail(error)
            const page = data as unknown as { data: User[]; total: number }
            return { items: page?.data ?? [], total: page?.total ?? 0 }
        },
    })
}

export function useDocumentSubjects(org: string, docUid: string, enabled = true) {
    return useQuery({
        queryKey: ['dp-subjects', org, docUid],
        enabled,
        queryFn: async (): Promise<DataSubject[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/documents/{documentUid}/subjects', {
                params: { path: { orgCode: org, documentUid: docUid } },
            })
            if (error) fail(error)
            return data ?? []
        },
    })
}

export function usePersonalData(org: string, userUid: string, enabled = true) {
    return useQuery({
        queryKey: ['dp-personal-data', org, userUid],
        enabled,
        queryFn: async (): Promise<PersonalData> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/users/{userUid}/personal-data', {
                params: { path: { orgCode: org, userUid } },
            })
            if (error) fail(error)
            return data as PersonalData
        },
    })
}

/** The last 31 days of disclosures of a document, or of a person's documents. */
export function useDisclosures(
    org: string,
    filter: { document_uid?: string; subject_user_uid?: string },
    enabled = true,
) {
    return useQuery({
        queryKey: ['dp-disclosures', org, filter],
        enabled,
        retry: false,
        queryFn: async (): Promise<DisclosureEvent[]> => {
            const to = new Date()
            const from = new Date(to.getTime() - 30 * 24 * 3600 * 1000)
            const { data, error, response } = await apiClient.GET('/api/v1/organizations/{orgCode}/document-disclosures/events', {
                params: {
                    path: { orgCode: org },
                    query: { ...filter, from: from.toISOString(), to: to.toISOString() },
                },
            })
            if (response.status === 403) throw new Error('consultation_disabled')
            if (error) fail(error)
            return data ?? []
        },
    })
}

// --- Erasure (lot 4) ---

export type ErasureDecision = 'keep' | 'restrict' | 'purge' | 'empty' | 'anonymize' | 'refuse'
export type ErasureDecisionInput = { key: string; decision: ErasureDecision; ground?: string; note?: string }

/** Grounds a decision beyond the proposal cites (internal/dataprotection.Grounds). */
export const ERASURE_GROUNDS = [
    'consent_withdrawn', 'no_longer_necessary', 'objection_upheld', 'unlawful_processing',
    'legal_erasure_obligation', 'obligation_lapsed', 'legal_claims', 'legal_obligation',
    'contract_performance', 'archiving_public_interest',
] as const

/** Decisions per item kind, least destructive first. */
export const ERASURE_DECISIONS: Record<string, ErasureDecision[]> = {
    document: ['keep', 'restrict', 'purge'],
    extracted_value: ['keep', 'empty'],
    person: ['keep', 'refuse', 'anonymize'],
}

const RANK: Record<string, number> = { keep: 0, refuse: 0, restrict: 1, purge: 2, empty: 2, anonymize: 2 }

/** A decision beyond the proposal needs a ground and a note. */
export function needsGround(proposal: string, decision: string): boolean {
    return (RANK[decision] ?? 0) > (RANK[proposal] ?? 0)
}

/** API error with its code, so the UI can tell `erasure_facts_changed` apart. */
export class ApiError extends Error {
    code?: string
    constructor(message: string, code?: string) {
        super(message)
        this.code = code
    }
}

function failCode(error: unknown): never {
    const e = error as { title?: string; message?: string; code?: string } | undefined
    throw new ApiError(e?.title || e?.message || 'Request failed', e?.code)
}

export function useErasurePreview(org: string, docUid: string, enabled: boolean) {
    return useQuery({
        queryKey: ['dp-erasure-preview', org, docUid],
        enabled,
        queryFn: async (): Promise<ErasurePreview> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/documents/{documentUid}/erasure-preview', {
                params: { path: { orgCode: org, documentUid: docUid } },
            })
            if (error) fail(error)
            return data as ErasurePreview
        },
    })
}

export function useErasureRequests(org: string, filter: { subject_user_uid?: string; document_uid?: string } = {}, enabled = true) {
    return useQuery({
        queryKey: ['dp-erasure-requests', org, filter],
        enabled,
        // A running request is followed until its job closes it.
        refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'running') ? 3000 : false),
        queryFn: async (): Promise<ErasureRequest[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/erasure-requests', {
                params: { path: { orgCode: org }, query: filter },
            })
            if (error) fail(error)
            return data ?? []
        },
    })
}

function useInvalidateErasure(org: string) {
    const qc = useQueryClient()
    return () => {
        void qc.invalidateQueries({ queryKey: ['dp-erasure-requests', org] })
        void qc.invalidateQueries({ queryKey: ['dp-personal-data', org] })
        void qc.invalidateQueries({ queryKey: ['dp-retention'] })
        void qc.invalidateQueries({ queryKey: ['dp-measures', org] })
    }
}

/** POST /erasure-requests: the Purge action (decided at once) or a retention-queue contact. */
export function useCreateErasureRequest(org: string) {
    const invalidate = useInvalidateErasure(org)
    return useMutation({
        mutationFn: async (body: {
            origin: 'retention' | 'manual'
            document_uid?: string
            user_uid?: string
            decision?: 'keep' | 'restrict' | 'purge'
        }) => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/erasure-requests', {
                params: { path: { orgCode: org } },
                body,
            })
            if (error) failCode(error)
            return data as ErasureRequest
        },
        onSuccess: invalidate,
    })
}

/** POST /users/{uid}/erasure-requests: a person's request. */
export function useCreateSubjectErasureRequest(org: string, userUid: string) {
    const invalidate = useInvalidateErasure(org)
    return useMutation({
        mutationFn: async (body: { received_at: string; channel: 'email' | 'letter' | 'web' | 'phone' | 'other' }) => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/users/{userUid}/erasure-requests', {
                params: { path: { orgCode: org, userUid } },
                body,
            })
            if (error) failCode(error)
            return data as ErasureRequest
        },
        onSuccess: invalidate,
    })
}

export function useUpdateErasureRequest(org: string) {
    const invalidate = useInvalidateErasure(org)
    return useMutation({
        mutationFn: async ({ uid, body }: {
            uid: string
            body: { decisions?: ErasureDecisionInput[]; reject?: boolean; recipients_note?: string }
        }) => {
            const { data, error } = await apiClient.PATCH('/api/v1/organizations/{orgCode}/erasure-requests/{requestUid}', {
                params: { path: { orgCode: org, requestUid: uid } },
                body,
            })
            if (error) failCode(error)
            return data as ErasureRequest
        },
        onSuccess: invalidate,
    })
}

export function useExecuteErasureRequest(org: string) {
    const invalidate = useInvalidateErasure(org)
    return useMutation({
        mutationFn: async (uid: string) => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/erasure-requests/{requestUid}/execute', {
                params: { path: { orgCode: org, requestUid: uid } },
            })
            if (error) failCode(error)
            return data as ErasureRequest
        },
        onSettled: invalidate,
    })
}

/** POST /data-protection-measures: a hold that extends a retention (the queue's "Extend"). */
export function useExtendRetention(org: string) {
    const invalidate = useInvalidateErasure(org)
    return useMutation({
        mutationFn: async (body: { document_uid?: string; user_uid?: string; ends_at: string; note?: string }) => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/data-protection-measures', {
                params: { path: { orgCode: org } },
                body: { kind: 'hold', reason: 'retention_extension', review_at: body.ends_at, ...body } as never,
            })
            if (error) failCode(error)
            return data as DataProtectionMeasure
        },
        onSuccess: invalidate,
    })
}
